<template>
  <div class="flex flex-col flex-1 overflow-y-auto w-full">
    <div class="px-4 rounded-xl">
      <!-- Hidden form to trick Chrome autofill -->
      <form style="display: none;">
        <input type="text" name="fakeusernameremembered" />
        <input type="password" name="fakepasswordremembered" />
      </form>
      
      <div class="mt-2 flex flex-col gap-4">
        <!-- Basic Information -->
        <BasicInfoSection
            :uname="uname"
            :fullname="fullname"
            :email="email"
            :phone="phone"
            :uname-rules="unameRules"
            :name-rules="nameRules"
            :email-rules="emailRules"
            :number-rules="numberRules"
            :confirm-uname-rules="confirmUnameRules"
            :confirm-uname-range="confirmUnameRange"
            :get-range-hints="getRangeHints"
            @update:uname="uname = $event"
            @update:fullname="fullname = $event"
            @update:email="email = $event"
            @update:phone="phone = $event"
          />

          <!-- Password Section -->
          <PasswordSection
            :password="password"
            :conf-password="confPassword"
            :show-pass="showPass"
            :show-conf="showConf"
            :password-rules="passwordRules"
            :confirm-password-rules="confirmPasswordRules"
            :password-confirmation-rule="passwordConfirmationRule"
            @update:password="password = $event"
            @update:conf-password="confPassword = $event"
            @update:show-pass="showPass = $event"
            @update:show-conf="showConf = $event"
          />

          <!-- User Type and Skills in same row -->
          <div class="grid grid-cols-2 gap-4">
            <UserTypeSelectionSection
              :user-type="userType"
              :limits="limits"
              :enable-admin-type="enableAdminType"
              @update:user-type="userType = $event"
            />
            <div class="flex flex-col gap-2">
              <label class="text-sm text-gray-700">
                {{ $t("users.create_user.title_skill_choose") }} 
                <span class="text-xs">{{ $t("users.create_user.title_skill_mandatory") }}</span>
              </label>
              <MultiSelect
                :model-value="skills"
                :options="skilsNames"
                :optionLabel="$i18n.locale == 'ar' ? 'titles.ar' : 'titles.en'"
                optionValue="name"
                :required="setSkillMandatory"
                class="w-full text-sm"
                @update:model-value="skills = $event"
              />
            </div>
          </div>

          <!-- Additional Settings -->
          <AdditionalSettingsSection
            :set-expiration="setExpiration"
            :validity-range="validityRange"
            @update:set-expiration="setExpiration = $event"
            @update:validity-range="validityRange = $event"
          />

          <!-- Create Button -->
          <div class="ma-1 mt-2 d-flex justify-end">
            <Button
              :disabled="!enableCreateBtn"
              class="bg-primary-900 text-white py-2 px-5 rounded-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer"
              size="large"
              @click="handleCreateUser"
            >
              {{ $t("users.create_user.create_btn") }}
            </Button>
          </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from 'vue';
import { useCreateUser } from '@/composables/useCreateUser';
import { Button, MultiSelect } from 'primevue';
import BasicInfoSection from './components/BasicInfoSection.vue';
import PasswordSection from './components/PasswordSection.vue';
import AdditionalSettingsSection from './components/AdditionalSettingsSection.vue';
import UserTypeSelectionSection from './components/UserTypeSelectionSection.vue';

export default {
  name: 'CreateUser',
  components: {
    Button,
    MultiSelect,
    BasicInfoSection,
    PasswordSection,
    AdditionalSettingsSection,
    UserTypeSelectionSection,
  },
  emits: ['closeDialog'],
  setup(props, { emit }) {
    const {
      // Form state
      setSkillMandatory,
      uname,
      validityRange,
      fullname,
      email,
      phone,
      password,
      confPassword,
      userType,
      skills,
      showPass,
      showConf,
      setExpiration,
      
      // Limit states
      isAgentLimit,
      noAgent,
      isReporterLimit,
      noReporter,
      isAdminLimit,
      noAdmin,
      inLimits,
      
      // Computed
      domainSkills,
      domainRanges,
      domainUsers,
      domain,
      permissions,
      editAdminPermission,
      getbaseURL,
      passwordConfirmationRule,
      confirmUnameRules,
      confirmUnameRange,
      existInRanges,
      getRangeHints,
      enableAdminType,
      enableCreateBtn,
      skilsNames,
      
      // Validation rules
      unameRules,
      passwordRules,
      nameRules,
      numberRules,
      emailRules,
      confirmPasswordRules,
      startDateRules,
      endDateRules,
      
      // Methods
      createUser,
      reset,
    } = useCreateUser();

    // Computed for limits object
    const limits = computed(() => ({
      isAgentLimit: isAgentLimit.value,
      noAgent: noAgent.value,
      isReporterLimit: isReporterLimit.value,
      noReporter: noReporter.value,
      isAdminLimit: isAdminLimit.value,
      noAdmin: noAdmin.value,
      inLimits: inLimits.value,
    }));

    const handleCreateUser = async () => {
      const success = await createUser();
      if (success) {
        emit('closeDialog');
      }
    };

    // Clear form when component is mounted
    onMounted(() => {
      reset();
    });

    return {
      // Form state
      setSkillMandatory,
      uname,
      validityRange,
      fullname,
      email,
      phone,
      password,
      confPassword,
      userType,
      skills,
      showPass,
      showConf,
      setExpiration,
      
      // Computed
      domainSkills,
      domainRanges,
      domainUsers,
      domain,
      permissions,
      editAdminPermission,
      getbaseURL,
      passwordConfirmationRule,
      confirmUnameRules,
      confirmUnameRange,
      existInRanges,
      getRangeHints,
      enableAdminType,
      enableCreateBtn,
      skilsNames,
      limits,
      
      // Validation rules
      unameRules,
      passwordRules,
      nameRules,
      numberRules,
      emailRules,
      confirmPasswordRules,
      startDateRules,
      endDateRules,
      
      // Methods
      handleCreateUser,
    };
  },
};
</script>

<style scoped>
.titles {
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.333;
  line-height: normal;
}
</style>
